import {
    CCPPricingPlanContent,
    CCPPricingPlanItem,
    CCPPricingPlanTier
} from "src/contentful";

import { PricingPlans, PricingStrategy, PricingTier } from "../constants";

const HAMS_PRICING_STRATEGY_BY_CCP_PRICING_POLICY = {
    ["VOLUME"]: PricingStrategy.FIXED_COST,
    ["GRADUATED"]: PricingStrategy.MARGINAL_PER_UNIT
};

// CCP Amounts are stored in cents - Note: JPY needs no conversion
const formatCentsToDollars = (value: number, currency: string): number => {
    if (currency === "USD" && value) {
        return value / 100;
    }
    return value;
};

const convertCCPPricingStrategyToHAMS = (
    ccpPricingPolicy: string
): keyof typeof PricingStrategy => {
    const hamsPricingStrategy =
        HAMS_PRICING_STRATEGY_BY_CCP_PRICING_POLICY[
            ccpPricingPolicy as "GRADUATED" | "VOLUME"
        ];

    if (!hamsPricingStrategy) {
        throw new Error(
            `Could not find HAMS pricing strategy to match CCP pricing policy: "${ccpPricingPolicy}"`
        );
    }

    return hamsPricingStrategy;
};

const convertCCPTiersToHAMS = (
    ccpTiers: CCPPricingPlanTier[],
    tiersMode: string,
    chargeElement: string,
    currency: "USD" | "JPY"
): PricingTier[] =>
    ccpTiers.map((ccpTier: CCPPricingPlanTier) => ({
        pricingStrategy: convertCCPPricingStrategyToHAMS(tiersMode),
        unitDescription: chargeElement,
        unitLimitLower: ccpTier.floor,
        unitLimitUpper: ccpTier.ceiling,
        prices: [
            {
                currency,
                unitPrice:
                    tiersMode === "GRADUATED"
                        ? formatCentsToDollars(ccpTier.unitAmount, currency)
                        : formatCentsToDollars(ccpTier.flatAmount, currency)
            }
        ]
    }));

export const convertCCPPricingPlansToHAMS = (
    pricingPlans: CCPPricingPlanContent[],
    currencyISOCode: string
): PricingPlans => {
    const [monthlyCCP, annualCCP] = ["MONTH", "YEAR"].map((primaryCycle) =>
        // Per CCP Team: We need to filter by COMMERCIAL, DEFAULT_PRICING
        pricingPlans.find(
            (pricingPlan) =>
                pricingPlan.primaryCycle_interval === primaryCycle &&
                pricingPlan.type === "COMMERCIAL" &&
                pricingPlan.activatedWithReason === "DEFAULT_PRICING" &&
                // We filter by currencyISOCode as we need both USD & JPY Pricing for Pricing Pages
                // If needed, can filter by USD only which we will pass in directly in those components
                pricingPlan.currency === currencyISOCode
        )
    );

    const [monthly, annual] = [monthlyCCP, annualCCP].map((ccpContent) => {
        if (!ccpContent) {
            return undefined;
        }

        const ccpTiers = ccpContent.items[0] as CCPPricingPlanItem;
        const { tiers, tiersMode, chargeElement } = ccpTiers;

        return convertCCPTiersToHAMS(
            tiers,
            tiersMode,
            chargeElement,
            ccpContent.currency as "USD" | "JPY"
        );
    });

    return {
        monthly,
        annual
    };
};
